"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useIntlayer } from "next-intlayer";
import { CourseCard } from "@/components/CourseCard";
import type { GetCoursesQueryResult } from "@/sanity.types";

interface CoursesSectionProps {
  courses: GetCoursesQueryResult;
}

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

export default function CoursesSection({ courses }: CoursesSectionProps) {
  const content = useIntlayer("courses-section");
  const featured = courses.slice(0, 3);

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-4 text-[#0193cd]">
            {content.label}
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-foreground">
            {content.heading}
          </h2>
          <p className="text-lg max-w-xl mx-auto text-muted-foreground">{content.subheading}</p>
        </motion.div>

        {/* Course cards */}
        {featured.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {featured.map((course) => (
              <motion.div key={course._id} variants={cardVariants}>
                <CourseCard course={course} href={`/courses/${course.slug}`} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-12 mb-12 text-muted-foreground">
            <p className="text-lg">{content.comingSoon}</p>
          </div>
        )}

        {/* View all CTA */}
        <div className="text-center">
          <Link
            href="/courses"
            className="inline-block px-8 py-3 rounded-full font-semibold border-2 transition-all hover:text-white hover:bg-[#0193cd] border-[#0193cd] text-[#0193cd]"
          >
            {content.viewAllCourses}
          </Link>
        </div>
      </div>
    </section>
  );
}
